import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ReservationDateTimeSelectorV2.css";
import ReservationSingleDayCalendarModal from "./ReservationSingleDayCalendarModal";
import {
    DAILY_MINIMUM_RENTAL_MS,
    MS_IN_HOUR,
    applyOpeningTimeToDate,
    calculateDayExtensionHours,
    cloneDate,
    formatDate,
    formatPopupDateTime,
    formatPopupDuration,
    formatTime,
    getScheduleEntryForDate,
    isSameCalendarDate,
    parseDateLike,
    resolveDailyOpeningStart,
    roundedDateWithInterval,
    setTimePortion,
} from "./ReservationDateTimeSelectorUtils";

const resolveDailyDefaultStart = (locationSchedule, stepMinutes) => {
    const today = new Date();
    const openingToday = applyOpeningTimeToDate(today, locationSchedule);
    if (openingToday) return openingToday;
    const nextOpening = resolveDailyOpeningStart(today, locationSchedule);
    if (nextOpening) return nextOpening;
    return roundedDateWithInterval(today, stepMinutes);
};

const resolveDailyDefaultEnd = (startDateTime) => (
    new Date(startDateTime.getTime() + (24 * MS_IN_HOUR))
);

const getStartOfDayTimestamp = (dateValue) => {
    const date = new Date(dateValue);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).getTime();
};

const minutesToTime = (minutes) => {
    const safeMinutes = Math.max(0, Math.min((24 * 60) - 1, minutes));
    const hour = String(Math.floor(safeMinutes / 60)).padStart(2, "0");
    const minute = String(safeMinutes % 60).padStart(2, "0");
    return `${hour}:${minute}`;
};

const timeToMinutes = (value) => {
    if (!value) return null;
    const [hour, minute] = String(value).split(":").map((part) => Number(part));
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return hour * 60 + minute;
};

const buildFullDayTimeOptions = (stepMinutes) => {
    const safeStep = Math.max(1, Math.round(stepMinutes));
    const options = [];
    for (let minutes = 0; minutes < 24 * 60; minutes += safeStep) {
        options.push(minutesToTime(minutes));
    }
    return options;
};

const getVisualScheduleRangeForDate = (date, schedule) => {
    const entry = getScheduleEntryForDate(date, schedule);
    if (!entry || entry.isClosed || entry.isOpen24h) return null;
    return {
        open: entry.openHour * 60 + entry.openMinute,
        close: entry.closeHour * 60 + entry.closeMinute,
    };
};

const isFullyClosedScheduleDay = (date, schedule) => {
    const entry = getScheduleEntryForDate(date, schedule);
    return Boolean(entry?.isClosed);
};

const isOutsideVisualRange = (minutes, range) => Boolean(
    range &&
    Number.isFinite(minutes) &&
    (minutes < range.open || minutes > range.close)
);

const buildInitialPeriodDaily = ({
    initialStart,
    initialEnd,
    stepMinutes,
    locationSchedule,
}) => {
    const parsedStart = parseDateLike(initialStart);
    const defaultStart = resolveDailyDefaultStart(locationSchedule, stepMinutes);
    const parsedEnd = parseDateLike(initialEnd);

    const start = parsedStart ? cloneDate(parsedStart) : cloneDate(defaultStart);
    const defaultEnd = resolveDailyDefaultEnd(start);
    const end = parsedEnd ? cloneDate(parsedEnd) : defaultEnd;

    return { start, end };
};

export default function ReservationDateTimeSelectorDaily({
    startTimeStepMinutes = 30,
    initialStart = null,
    initialEnd = null,
    locationSchedule = [],
    onPeriodChange,
    onOutsideScheduleChange,
    children = null,
}) {
    const [isStartDateModalOpen, setIsStartDateModalOpen] = useState(false);
    const [isStartTimeModalOpen, setIsStartTimeModalOpen] = useState(false);
    const [isEndModalOpen, setIsEndModalOpen] = useState(false);
    const [isAutoCorrectionModalOpen, setIsAutoCorrectionModalOpen] = useState(false);
    const [isEndConfirmBlinking, setIsEndConfirmBlinking] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const endConfirmBlinkTimeoutRef = useRef(null);

    const resolvedInitialPeriod = useMemo(
        () => buildInitialPeriodDaily({
            initialStart,
            initialEnd,
            stepMinutes: startTimeStepMinutes,
            locationSchedule,
        }),
        [
            initialStart,
            initialEnd,
            startTimeStepMinutes,
            locationSchedule,
        ]
    );

    const [start, setStart] = useState(() => cloneDate(resolvedInitialPeriod.start));
    const [end, setEnd] = useState(() => cloneDate(resolvedInitialPeriod.end));
    const [tempStart, setTempStart] = useState(() => cloneDate(resolvedInitialPeriod.start));
    const [tempEnd, setTempEnd] = useState(() => cloneDate(resolvedInitialPeriod.end));

    const todayStartTimestamp = getStartOfDayTimestamp(new Date());
    const startDayTimestamp = getStartOfDayTimestamp(start);
    const dailyMinimumEndTimestamp = Math.max(
        start.getTime() + DAILY_MINIMUM_RENTAL_MS,
        Date.now()
    );
    const invalidStart = startDayTimestamp < todayStartTimestamp;
    const invalidEnd = end.getTime() < dailyMinimumEndTimestamp;

    const fullDayTimeOptions = useMemo(
        () => buildFullDayTimeOptions(startTimeStepMinutes),
        [startTimeStepMinutes]
    );

    const applyStartChange = (newStartValue) => {
        const requestedStart = cloneDate(newStartValue);
        const didDateChange = !isSameCalendarDate(requestedStart, start);
        setStart(requestedStart);

        if (didDateChange) {
            const nextEnd = resolveDailyDefaultEnd(requestedStart);
            setEnd(nextEnd);
            setSnackbarMessage("Return date/time adjusted automatically");
        }
    };

    useEffect(() => {
        setStart(cloneDate(resolvedInitialPeriod.start));
        setEnd(cloneDate(resolvedInitialPeriod.end));
        setTempStart(cloneDate(resolvedInitialPeriod.start));
        setTempEnd(cloneDate(resolvedInitialPeriod.end));
    }, [resolvedInitialPeriod]);

    useEffect(() => {
        if (typeof onPeriodChange === "function") {
            onPeriodChange({ start, end });
        }
    }, [start, end, onPeriodChange]);

    useEffect(() => {
        const tick = () => {
            const shouldAutoCorrect = getStartOfDayTimestamp(start) < getStartOfDayTimestamp(new Date());
            if (shouldAutoCorrect) {
                setIsAutoCorrectionModalOpen(true);
            }
        };

        tick();
        const intervalId = window.setInterval(tick, 1000);
        return () => window.clearInterval(intervalId);
    }, [start, startTimeStepMinutes]);

    useEffect(() => {
        if (!snackbarMessage) return undefined;
        const timeoutId = window.setTimeout(() => {
            setSnackbarMessage("");
        }, 2800);
        return () => window.clearTimeout(timeoutId);
    }, [snackbarMessage]);

    useEffect(() => () => {
        if (endConfirmBlinkTimeoutRef.current) {
            window.clearTimeout(endConfirmBlinkTimeoutRef.current);
        }
    }, []);

    const triggerEndConfirmBlink = () => {
        setIsEndConfirmBlinking(false);
        if (endConfirmBlinkTimeoutRef.current) {
            window.clearTimeout(endConfirmBlinkTimeoutRef.current);
            endConfirmBlinkTimeoutRef.current = null;
        }

        window.requestAnimationFrame(() => {
            setIsEndConfirmBlinking(true);
        });

        endConfirmBlinkTimeoutRef.current = window.setTimeout(() => {
            setIsEndConfirmBlinking(false);
            endConfirmBlinkTimeoutRef.current = null;
        }, 1400);
    };

    const handleStartDateOpen = () => {
        setTempStart(cloneDate(start));
        setIsStartDateModalOpen(true);
    };

    const handleStartTimeOpen = () => {
        const normalized = roundedDateWithInterval(start, startTimeStepMinutes);
        setTempStart(normalized);
        setIsStartDateModalOpen(false);
        setIsStartTimeModalOpen(true);
    };

    const handleEndModalOpen = () => {
        setIsStartDateModalOpen(false);
        setIsEndConfirmBlinking(false);
        setTempEnd(cloneDate(end));
        setIsEndModalOpen(true);
    };

    const handleStartDateConfirm = (selectedDate) => {
        const nextWithSelectedDay = setTimePortion(
            selectedDate,
            tempStart.getHours(),
            tempStart.getMinutes()
        );
        const nextWithOpening = applyOpeningTimeToDate(nextWithSelectedDay, locationSchedule) ||
            nextWithSelectedDay;
        setTempStart(nextWithOpening);
        applyStartChange(nextWithOpening);
        setIsStartDateModalOpen(false);
    };

    const handleStartTimeConfirm = () => {
        applyStartChange(tempStart);
        setIsStartTimeModalOpen(false);
    };

    const handleStartTimeCancel = () => {
        setTempStart(cloneDate(start));
        setIsStartTimeModalOpen(false);
    };

    const handleEndModalCancel = () => {
        setIsEndConfirmBlinking(false);
        setTempEnd(cloneDate(end));
        setIsEndModalOpen(false);
    };

    const handleEndConfirm = () => {
        setIsEndConfirmBlinking(false);
        setEnd(cloneDate(tempEnd));
        setIsEndModalOpen(false);
    };

    const handleAutoCorrectionConfirm = () => {
        const nextMinimumStart = resolveDailyDefaultStart(locationSchedule, startTimeStepMinutes);
        const duration = end.getTime() - start.getTime();
        const nextStart = cloneDate(nextMinimumStart);
        const nextEnd = new Date(nextStart.getTime() + duration);

        setStart(nextStart);
        setEnd(nextEnd);
        setSnackbarMessage("Return date/time adjusted automatically");
        setIsAutoCorrectionModalOpen(false);
    };

    const defaultEndForCurrentStart = useMemo(
        () => resolveDailyDefaultEnd(start),
        [start]
    );

    const startVisualRange = useMemo(
        () => getVisualScheduleRangeForDate(start, locationSchedule),
        [start, locationSchedule]
    );
    const endVisualRange = useMemo(
        () => getVisualScheduleRangeForDate(end, locationSchedule),
        [end, locationSchedule]
    );
    const tempStartVisualRange = useMemo(
        () => getVisualScheduleRangeForDate(tempStart, locationSchedule),
        [tempStart, locationSchedule]
    );
    const isStartClosedDay = useMemo(
        () => isFullyClosedScheduleDay(start, locationSchedule),
        [start, locationSchedule]
    );
    const isEndClosedDay = useMemo(
        () => isFullyClosedScheduleDay(end, locationSchedule),
        [end, locationSchedule]
    );
    const isTempStartClosedDay = useMemo(
        () => isFullyClosedScheduleDay(tempStart, locationSchedule),
        [tempStart, locationSchedule]
    );

    const tempEndDuration = tempEnd.getTime() - start.getTime();
    const tempStartMinutes = tempStart.getHours() * 60 + tempStart.getMinutes();
    const tempStartTimeValue = minutesToTime(tempStartMinutes);
    const isTempStartUnchanged = tempStart.getTime() === start.getTime();
    const isTempEndUnchanged = tempEnd.getTime() === end.getTime();
    const isTempStartInvalid = getStartOfDayTimestamp(tempStart) < todayStartTimestamp;
    const isTempEndBelowMinimum = tempEnd.getTime() < dailyMinimumEndTimestamp;
    const isTempEndDifferentFromDefault = tempEnd.getTime() !== defaultEndForCurrentStart.getTime();
    const isTempStartOutsideVisual = isTempStartClosedDay || isOutsideVisualRange(tempStartMinutes, tempStartVisualRange);

    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();
    const isStartTimeOutsideVisual = isOutsideVisualRange(startMinutes, startVisualRange);
    const isEndTimeOutsideVisual = isOutsideVisualRange(endMinutes, endVisualRange);
    const isStartOutsideVisual = isStartClosedDay || isStartTimeOutsideVisual;
    const isEndOutsideVisual = isEndClosedDay || isEndTimeOutsideVisual;
    const showOutsideScheduleWarning = isStartOutsideVisual || isEndOutsideVisual;

    useEffect(() => {
        if (typeof onOutsideScheduleChange === "function") {
            onOutsideScheduleChange(showOutsideScheduleWarning);
        }
    }, [showOutsideScheduleWarning, onOutsideScheduleChange]);

    const oneDayExtensionHours = calculateDayExtensionHours(start, tempEnd);
    const canDecreaseDailyByHour = tempEnd.getTime() - MS_IN_HOUR >= dailyMinimumEndTimestamp;
    const canDecreaseDailyByDay = tempEnd.getTime() - oneDayExtensionHours * MS_IN_HOUR >= dailyMinimumEndTimestamp;

    const shiftTempEndBy = (deltaMs) => {
        setTempEnd((previous) => {
            const next = new Date(previous.getTime() + deltaMs);
            const minimumDailyEnd = Math.max(start.getTime() + DAILY_MINIMUM_RENTAL_MS, Date.now());
            if (next.getTime() < minimumDailyEnd) {
                return new Date(minimumDailyEnd);
            }
            return next;
        });
        triggerEndConfirmBlink();
    };

    const endDateFieldTextClass = invalidEnd
        ? "kav-rdt2__value is-error"
        : (isEndClosedDay ? "kav-rdt2__value is-muted" : "kav-rdt2__value");
    const endTimeFieldTextClass = invalidEnd
        ? "kav-rdt2__value is-error"
        : (isEndOutsideVisual ? "kav-rdt2__value is-muted" : "kav-rdt2__value");
    const startDateFieldTextClass = invalidStart
        ? "kav-rdt2__value is-error"
        : (isStartClosedDay ? "kav-rdt2__value is-muted" : "kav-rdt2__value");
    const startTimeFieldTextClass = invalidStart
        ? "kav-rdt2__value is-error"
        : (isStartOutsideVisual ? "kav-rdt2__value is-muted" : "kav-rdt2__value");

    return (
        <div className="kav-rdt2">
            <div className="kav-rdt2__header">
                {/*<h3>Date &amp; Time</h3>*/}
                {/*<button*/}
                {/*    type="button"*/}
                {/*    className="kav-rdt2__resetBtn"*/}
                {/*    onClick={applyPeriodReset}*/}
                {/*>*/}
                {/*    Reset*/}
                {/*</button>*/}
            </div>

            <div className="kav-rdt2__fromCalendarWrap">
                <div className="kav-rdt2__row">
                    <div className="kav-rdt2__label">From</div>
                    <div className="kav-rdt2__fromFields">
                        <button
                            type="button"
                            className="kav-rdt2__fieldBtn"
                            onClick={handleStartDateOpen}
                        >
                            <i className="bi bi-calendar-date kav-rdt2__icon" aria-hidden="true"></i>
                            <span className={startDateFieldTextClass}>{formatDate(start)}</span>
                        </button>

                        <button
                            type="button"
                            className="kav-rdt2__fieldBtn"
                            onClick={handleStartTimeOpen}
                        >
                            <i className="bi bi-clock kav-rdt2__icon" aria-hidden="true"></i>
                            <span className={startTimeFieldTextClass}>{formatTime(start)}</span>
                        </button>
                    </div>
                </div>

                <ReservationSingleDayCalendarModal
                    isOpen={isStartDateModalOpen}
                    selectedDate={tempStart}
                    minDate={new Date()}
                    locationSchedule={locationSchedule}
                    onRequestClose={() => setIsStartDateModalOpen(false)}
                    onConfirm={handleStartDateConfirm}
                />
            </div>

            <div className="kav-rdt2__row kav-rdt2__row--single">
                <div className="kav-rdt2__label">To</div>
                <button
                    type="button"
                    className="kav-rdt2__fieldBtn kav-rdt2__fieldBtn--wide"
                    onClick={handleEndModalOpen}
                >
                    <i className="bi bi-calendar-date kav-rdt2__icon" aria-hidden="true"></i>
                    <span className={endDateFieldTextClass}>{formatDate(end)}</span>
                    <i className="bi bi-clock kav-rdt2__icon kav-rdt2__icon--right" aria-hidden="true"></i>
                    <span className={endTimeFieldTextClass}>{formatTime(end)}</span>
                </button>
            </div>

            <button
                type="button"
                className="kav-rdt2__changePeriodCta"
                onClick={handleEndModalOpen}
            >
                KLIK HIER OM UREN EN DAGEN TE VERANDEREN
            </button>

            {invalidStart ? (
                <p className="kav-rdt2__errorText">
                    Pick-up date cannot be in the past.
                </p>
            ) : null}

            {invalidEnd ? (
                <p className="kav-rdt2__errorText">
                    Return time cannot be in the past and must be at least 1 hour after pick-up.
                </p>
            ) : null}

            {isStartTimeModalOpen ? (
                <div className="kav-rdt2-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-rdt2-modal__backdrop"
                        onClick={handleStartTimeCancel}
                        aria-label="Close"
                    />
                    <div className="kav-rdt2-modal__card kav-rdt2-modal__card--startTime">
                        <h4>Pick-up time</h4>

                        <select
                            className={`kav-rdt2-select ${isTempStartOutsideVisual ? "kav-rdt2-select--outside" : ""}`}
                            value={tempStartTimeValue}
                            onChange={(event) => {
                                const nextMinutes = timeToMinutes(event.target.value);
                                if (!Number.isFinite(nextMinutes)) return;
                                const nextHour = Math.floor(nextMinutes / 60);
                                const nextMinute = nextMinutes % 60;
                                setTempStart(setTimePortion(tempStart, nextHour, nextMinute));
                            }}
                        >
                            {fullDayTimeOptions.map((option) => {
                                const optionMinutes = timeToMinutes(option);
                                const isOutside = isOutsideVisualRange(optionMinutes, tempStartVisualRange);
                                return (
                                    <option
                                        key={`start-time-${option}`}
                                        value={option}
                                        style={isOutside ? { color: "#9a9a9a" } : undefined}
                                    >
                                        {option}
                                    </option>
                                );
                            })}
                        </select>

                        {isTempStartOutsideVisual ? (
                            <p className="kav-rdt2__warningText">
                                Selected time is outside location opening hours.
                            </p>
                        ) : null}

                        <div className="kav-rdt2-modal__actions">
                            <button type="button" className="kav-rdt2-btn kav-rdt2-btn--ghost" onClick={handleStartTimeCancel}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--primary"
                                onClick={handleStartTimeConfirm}
                                disabled={isTempStartUnchanged || isTempStartInvalid}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {isEndModalOpen ? (
                <div className="kav-rdt2-modal" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="kav-rdt2-modal__backdrop"
                        onClick={handleEndModalCancel}
                        aria-label="Close"
                    />
                    <div className="kav-rdt2-modal__card">
                        <button
                            type="button"
                            className="kav-rdt2-modal__close"
                            onClick={handleEndModalCancel}
                            aria-label="Close"
                        >
                            <i className="bi bi-x-lg" aria-hidden="true"></i>
                        </button>

                        <div className="kav-rdt2-endSummary">
                            <div className="kav-rdt2-endSummary__block">
                                <p className="kav-rdt2-endSummary__label">Pick-up time</p>
                                <p className="kav-rdt2-endSummary__value">{formatPopupDateTime(start)}</p>
                            </div>
                            <div className="kav-rdt2-endSummary__block">
                                <p className="kav-rdt2-endSummary__label">Return time</p>
                                <p className="kav-rdt2-endSummary__value">{formatPopupDateTime(tempEnd)}</p>
                            </div>
                            <p
                                className="kav-rdt2-endSummary__duration"
                                style={{ color: "var(--wl-primary)", fontWeight: 800 }}
                            >
                                {formatPopupDuration(tempEndDuration)}
                            </p>
                        </div>

                        <div className="kav-rdt2-quickActions">
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--ghost"
                                onClick={() => shiftTempEndBy(-MS_IN_HOUR)}
                                disabled={!canDecreaseDailyByHour}
                            >
                                -1 Hour
                            </button>
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--ghost"
                                onClick={() => shiftTempEndBy(-(oneDayExtensionHours * MS_IN_HOUR))}
                                disabled={!canDecreaseDailyByDay}
                            >
                                -1 Day
                            </button>
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--ghost"
                                onClick={() => shiftTempEndBy(MS_IN_HOUR)}
                            >
                                +1 Hour
                            </button>
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--ghost"
                                onClick={() => {
                                    const extensionHours = calculateDayExtensionHours(start, tempEnd);
                                    shiftTempEndBy(extensionHours * MS_IN_HOUR);
                                }}
                            >
                                +1 Day
                            </button>
                        </div>

                        {isTempEndBelowMinimum ? (
                            <p className="kav-rdt2__errorText">
                                Return time must be at least 1 hour after pick-up and cannot be in the past.
                            </p>
                        ) : null}

                        <div className="kav-rdt2-modal__actions kav-rdt2-modal__actions--center">
                            <button
                                type="button"
                                className={`kav-rdt2-btn kav-rdt2-btn--confirmWide${isEndConfirmBlinking ? " kav-rdt2-btn--confirmWideBlink" : ""}`}
                                onClick={handleEndConfirm}
                                disabled={isTempEndUnchanged || isTempEndBelowMinimum}
                            >
                                Confirm {formatPopupDateTime(tempEnd)}
                            </button>
                        </div>

                        {isTempEndDifferentFromDefault ? (
                            <button
                                type="button"
                                className="kav-rdt2-btn kav-rdt2-btn--ghost kav-rdt2-btn--resetInline"
                                onClick={() => setTempEnd(cloneDate(defaultEndForCurrentStart))}
                            >
                                Reset
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {isAutoCorrectionModalOpen ? (
                <div className="kav-rdt2-modal" role="dialog" aria-modal="true">
                    <div className="kav-rdt2-modal__card">
                        <h4>Vertrektijd wordt aangepast</h4>
                        <p>
                            De vertrektijd ligt in het verleden. Druk ‘OK’ om de eerstvolgende vertrektijd te
                            selecteren. De opgegeven reserveringstijd wordt aangehouden.
                        </p>
                        <div className="kav-rdt2-modal__actions kav-rdt2-modal__actions--single">
                            <button type="button" className="kav-rdt2-btn kav-rdt2-btn--primary" onClick={handleAutoCorrectionConfirm}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {snackbarMessage ? (
                <div className="kav-rdt2-snackbar" role="status" aria-live="polite">
                    {snackbarMessage}
                </div>
            ) : null}

            {children}
        </div>
    );
}
